import { People } from "@material-ui/icons";
import {
  eventIconClasses,
  EventSchedulingQueryAttendee,
  EventSchedulingQueryEvent,
  EventSchedulingQueryOrg,
  EventSchedulingQueryPerson,
  getOrderedAttendeesAndPersonsMaps,
  MAX_NUM_OF_AVATARS_SHOWN,
  PersonImpact,
} from "./EventScheduling";
import React from "react";
import ReactTooltip from "react-tooltip";
import FocusTimeToolTip, { getName } from "./FocusTimeTooltip";
import Avatar from "./Avatar";

const TOOLTIP_ID = "EventAttendeesAvatarsToolTip";

const AvatarWithFocusTimeTooltip = ({
  org,
  personImpact,
  person,
  attendee,
}: {
  org: EventSchedulingQueryOrg;
  personImpact: PersonImpact;
  person: EventSchedulingQueryPerson;
  attendee: EventSchedulingQueryAttendee;
}) => {
  const { impacts } = personImpact;

  return (
    <Avatar
      org={org}
      person={person}
      tipText={<FocusTimeToolTip name={getName(person, personImpact)} impacts={impacts} />}
      calendarId={personImpact.calendarId}
      responseStatus={attendee && attendee.responseStatus}
      size={24}
    />
  );
};

const EventAttendeesAvatars = ({
  org,
  event,
}: {
  org?: EventSchedulingQueryOrg | null;
  event: EventSchedulingQueryEvent | undefined;
}) => {
  if (!event || !org) {
    return null;
  }

  const { personImpacts, personMap, attendeeMap } = getOrderedAttendeesAndPersonsMaps({
    org,
    event: event,
  });

  const attendeeCount = personImpacts.length;
  const maxRenderedAvatarsWithOverflow =
    MAX_NUM_OF_AVATARS_SHOWN - (attendeeCount > MAX_NUM_OF_AVATARS_SHOWN ? 1 : 0);

  const overflow = personImpacts.slice(maxRenderedAvatarsWithOverflow, 8); // render a few calendarIds in the remainder tooltip

  const personImpactsShown = personImpacts.slice(0, maxRenderedAvatarsWithOverflow); // grab first few calIds to render
  const numOfPersonImpactsShown = personImpactsShown.length;

  return (
    <div className="cw-flex cw-items-center">
      <People className={eventIconClasses} />
      {personImpactsShown.map((personImpact) => {
        const person = personMap[personImpact.calendarId] || null;
        const attendee = attendeeMap[personImpact.calendarId] || null;
        return (
          <AvatarWithFocusTimeTooltip
            key={personImpact.calendarId}
            org={org}
            personImpact={personImpact}
            person={person}
            attendee={attendee}
          />
        );
      })}
      {attendeeCount - numOfPersonImpactsShown > 0 && (
        <div
          className="cw-flex cw-overflow-hidden cw-flex-shrink-0 cw-rounded-full cw-items-center cw-justify-center cw-text-white cw-bg-gray-400 cw-h-6 cw-w-6 cw-font-semibold"
          data-for={TOOLTIP_ID}
          data-tip
        >
          +{attendeeCount - maxRenderedAvatarsWithOverflow}
        </div>
      )}
      <ReactTooltip multiline effect="solid" type="dark" id={TOOLTIP_ID}>
        {overflow.map((personImpact) => (
          <div key={personImpact.calendarId}>{personImpact.calendarId}</div>
        ))}
        {attendeeCount - 8 > 0 && <div>+{attendeeCount - 8} more</div>}
      </ReactTooltip>
    </div>
  );
};

export default EventAttendeesAvatars;
